// ─────────────────────────────────────────────────────────────
// Toast — Non-blocking notifications (replaces alert())
// XSS-safe: uses textContent, not innerHTML
// ─────────────────────────────────────────────────────────────
import { TOAST_DURATION } from '@/constants';

type ToastType = 'success' | 'error' | 'info' | 'warn';

interface ToastOptions {
  duration?: number;
  type?:     ToastType;
}

let container: HTMLElement | null = null;

function getContainer(): HTMLElement {
  if (!container) {
    container        = document.createElement('div');
    container.id     = 'toast-container';
    container.style.cssText = [
      'position:fixed', 'bottom:5rem', 'left:50%', 'transform:translateX(-50%)',
      'display:flex', 'flex-direction:column', 'align-items:center',
      'gap:8px', 'z-index:9999', 'pointer-events:none',
    ].join(';');
    document.body.appendChild(container);
  }
  return container;
}

const TYPE_STYLES: Record<ToastType, string> = {
  success: 'background:#0F6B3C;color:#fff',
  error:   'background:#C0172A;color:#fff',
  warn:    'background:#92520A;color:#fff',
  info:    'background:#006FA3;color:#fff',
};

const TYPE_ICONS: Record<ToastType, string> = {
  success: '✓', error: '✕', warn: '⚠', info: 'ℹ',
};

export function toast(message: string, options: ToastOptions = {}): void {
  const { duration = TOAST_DURATION.NORMAL, type = 'success' } = options;
  const c  = getContainer();
  const el = document.createElement('div');

  el.style.cssText = [
    TYPE_STYLES[type],
    'padding:10px 18px', 'border-radius:10px',
    'font-size:13px', 'font-weight:500',
    'box-shadow:0 4px 16px rgba(0,0,0,.2)',
    'display:flex', 'align-items:center', 'gap:8px',
    'opacity:0', 'transform:translateY(8px)',
    'transition:all .2s ease', 'pointer-events:auto',
    'font-family:var(--font)', 'max-width:90vw', 'text-align:center',
  ].join(';');

  // Use textContent to prevent XSS — never innerHTML
  const icon = document.createElement('span');
  icon.textContent = TYPE_ICONS[type];

  const text = document.createElement('span');
  text.textContent = message;   // Safe: no HTML injection possible

  el.appendChild(icon);
  el.appendChild(text);
  c.appendChild(el);

  requestAnimationFrame(() => {
    el.style.opacity   = '1';
    el.style.transform = 'translateY(0)';
  });

  setTimeout(() => {
    el.style.opacity   = '0';
    el.style.transform = 'translateY(8px)';
    setTimeout(() => el.remove(), 200);
  }, duration);
}

export const toastSuccess = (msg: string) => toast(msg, { type: 'success' });
export const toastError   = (msg: string) => toast(msg, { type: 'error', duration: TOAST_DURATION.LONG });
export const toastWarn    = (msg: string) => toast(msg, { type: 'warn' });
export const toastInfo    = (msg: string) => toast(msg, { type: 'info' });
